import { Moon, Sun } from 'lucide-react';
import { useEffect, useLayoutEffect, useState } from 'react';

export type ColorThemePreference = 'system' | 'light' | 'dark';
type ResolvedColorTheme = Exclude<ColorThemePreference, 'system'>;

export const THEME_STORAGE_KEY = 'amoye.colorTheme';
const THEME_CHANGE_EVENT = 'amoye-theme-change';

function initialPreference(): ColorThemePreference {
  if (typeof window === 'undefined') return 'system';
  try {
    const storedTheme = window.localStorage.getItem(THEME_STORAGE_KEY);
    if (storedTheme === 'system' || storedTheme === 'light' || storedTheme === 'dark') return storedTheme;
  } catch { /* Fall back to a theme applied before the app mounted. */ }
  return 'system';
}

function resolvedTheme(preference: ColorThemePreference): ResolvedColorTheme {
  if (preference !== 'system') return preference;
  return window.matchMedia?.('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
}

function applyTheme(theme: ResolvedColorTheme) {
  document.documentElement.dataset.theme = theme;
  document.documentElement.style.colorScheme = theme;
  document.querySelector('meta[name="theme-color"]')?.setAttribute('content', theme === 'dark' ? '#0c0e12' : '#f7f7f5');
}

function persistPreference(preference: ColorThemePreference) {
  try { window.localStorage.setItem(THEME_STORAGE_KEY, preference); } catch { /* The preference still applies for this page. */ }
  applyTheme(resolvedTheme(preference));
  window.dispatchEvent(new CustomEvent<ColorThemePreference>(THEME_CHANGE_EVENT, { detail: preference }));
}

export function useThemePreference() {
  const [preference, setPreference] = useState<ColorThemePreference>(initialPreference);

  useLayoutEffect(() => {
    applyTheme(resolvedTheme(preference));
  }, [preference]);

  useEffect(() => {
    const synchronize = (event: Event) => setPreference((event as CustomEvent<ColorThemePreference>).detail);
    const media = window.matchMedia?.('(prefers-color-scheme: dark)');
    const followSystem = () => { if (initialPreference() === 'system') applyTheme(resolvedTheme('system')); };
    window.addEventListener(THEME_CHANGE_EVENT, synchronize);
    media?.addEventListener('change', followSystem);
    return () => {
      window.removeEventListener(THEME_CHANGE_EVENT, synchronize);
      media?.removeEventListener('change', followSystem);
    };
  }, []);

  const update = (next: ColorThemePreference) => {
    persistPreference(next);
    setPreference(next);
  };
  return [preference, update] as const;
}

export function ThemeToggle() {
  const [preference, setPreference] = useThemePreference();
  const dark = resolvedTheme(preference) === 'dark';

  function toggleTheme() {
    setPreference(dark ? 'light' : 'dark');
  }

  return (
    <button
      aria-label={dark ? 'Switch to light theme' : 'Switch to dark theme'}
      aria-pressed={dark}
      className="icon-button theme-toggle"
      onClick={toggleTheme}
      title={dark ? 'Use light theme' : 'Use dark theme'}
      type="button"
    >
      {dark ? <Sun aria-hidden="true" size={17} /> : <Moon aria-hidden="true" size={17} />}
    </button>
  );
}

export function ThemePreferenceSelector() {
  const [preference, setPreference] = useThemePreference();
  const options: Array<{ value: ColorThemePreference; label: string }> = [
    { value: 'system', label: 'Use device setting' },
    { value: 'light', label: 'Light' },
    { value: 'dark', label: 'Dark' },
  ];
  return <fieldset className="theme-preference-selector">
    <legend>Theme</legend>
    {options.map((option) => <label key={option.value}><input checked={preference === option.value}
      name="amoye-theme-preference" onChange={() => setPreference(option.value)} type="radio" />
      <span>{option.label}</span></label>)}
  </fieldset>;
}
